import * as THREE from 'three/webgpu';
import { isSketchRelationContainer } from '../solver/constraint/SketchRelationContainer';
import { SketchRelationKind } from '../solver/constraint/relation-compatibility/SketchRelationKinds';
import type { SketchRelationDefinition } from '../solver/constraint/relationDefinition/SketchRelationDefinition';
import {LineHorizontalConstraint} from '../solver/constraint/relationDefinition/line/LineHorizontalConstraint';
import {LineVerticalConstraint} from '../solver/constraint/relationDefinition/line/LineVerticalConstraint';
import {SketchGeometryTransformConstraintPolicy} from './SketchGeometryTransformConstraintPolicy';
import {
    isAbstractSketchEdgeObject,
    isSketchCollectionObject,
    isSketchLineObject,
    isSketchMirrorAxisReferenceProvider,
    isSketchObject,
    isSketchPointObject,
    isSketchSlotObject,
    type ISketchObject,
    type SketchGroup,
} from '../../mesh/sketch';
import type {ReferencePlane} from '../../reference/ReferencePlane';
import type {SketchMirrorAxisResolvedGeometry} from '../../../types/sketch/mirror/SketchMirrorAxisReferenceTypes';

//------ 移动实体保留几何关系 开始------
/**
 * 几何关系断开模式。
 */
export enum SketchRelationRetentionDetachMode {
    /** 仅断开所选对象与未选对象之间的跨选择域关系。 */
    ExternalRelations = 'external-relations',
    /** 断开旋转实体时会阻止刚体旋转的关系。 */
    RotateBlockingRelations = 'rotate-blocking-relations',
    /** 断开原位反射后不能原样成立的绝对方向关系。 */
    ReflectionVariantRelations = 'reflection-variant-relations',
}

/**
 * 旋转实体默认不保留关系时需要断开的自关系。
 */
const ROTATE_BLOCKING_SELF_RELATION_KINDS: ReadonlySet<SketchRelationKind> = new Set([
    SketchRelationKind.Horizontal,
    SketchRelationKind.Vertical,
    SketchRelationKind.Fix,
    SketchRelationKind.FixSlot,
]);

/**
 * 几何关系保留上下文。
 */
export interface SketchRelationRetentionContext {
    /** 本次移除的约束数量。 */
    readonly removedConstraintCount: number;
    /** 确认移除结果，不再允许回滚。 */
    commit(): void;
    /** 回滚本次移除的约束。 */
    rollback(): void;
}

/**
 * 几何关系保留服务输入。
 */
interface SketchRelationRetentionInput {
    /** 当前活动草图组。 */
    readonly sketchGroup: SketchGroup;
    /** 当前命令选择域中的实体。 */
    readonly selectedEntities: readonly THREE.Object3D[];
    /** 本次断开关系时使用的策略。 */
    readonly detachMode?: SketchRelationRetentionDetachMode;
}

/**
 * 被临时移除的约束记录。
 */
interface RemovedConstraintRecord {
    /** 被移除的约束实例。 */
    readonly constraint: SketchRelationDefinition<ISketchObject>;
    /** 删除前持有该约束的对象。 */
    readonly owners: readonly ISketchObject[];
}

/**
 * 草图实体变换几何关系保留服务。
 *
 * 设计说明：
 * 1. SOLIDWORKS 取消 `Keep relations` 时，只断开所选实体与未选实体之间的关系；
 * 2. Rotate Entities 对水平、竖直、固定这类绝对方向/位置关系有额外处理，否则矩形会被约束拉回原方向；
 * 3. 选择域内部形状关系默认继续保留，服务只处理约束边界判断和可回滚删除，不负责实体位移、历史事务或 UI 状态。
 */
export class SketchRelationRetentionService {
    /** 几何变换关系的统一准入策略。 */
    private readonly transformConstraintPolicy = new SketchGeometryTransformConstraintPolicy();
    /**
     * 断开当前选择域与外部对象之间的几何关系。
     *
     * @param input 几何关系保留输入。
     * @returns 可提交或回滚的关系移除上下文。
     */
    public detachExternalRelations(input: SketchRelationRetentionInput): SketchRelationRetentionContext {
        return this.detachRelations({
            ...input,
            detachMode: SketchRelationRetentionDetachMode.ExternalRelations,
        });
    }

    /**
     * 断开旋转实体默认不保留关系时会阻止旋转的几何关系。
     *
     * @param input 几何关系保留输入。
     * @returns 可提交或回滚的关系移除上下文。
     */
    public detachRotateBlockingRelations(input: SketchRelationRetentionInput): SketchRelationRetentionContext {
        return this.detachRelations({
            ...input,
            detachMode: SketchRelationRetentionDetachMode.RotateBlockingRelations,
        });
    }

    /**
     * 断开原位镜像后需要换型或失效的绝对方向关系。
     * @param input 几何关系保留输入。
     * @param referencePlane 当前草图参考面。
     * @param axisGeometry 本次镜像轴世界几何。
     * @returns 可提交或回滚的关系移除上下文。
     */
    public detachReflectionVariantRelations(
        input: SketchRelationRetentionInput,
        referencePlane: ReferencePlane,
        axisGeometry: SketchMirrorAxisResolvedGeometry,
    ): SketchRelationRetentionContext {
        return this.detachRelations({
            ...input,
            detachMode: SketchRelationRetentionDetachMode.ReflectionVariantRelations,
        }, referencePlane, axisGeometry);
    }

    /**
     * 构建镜像副本的关系复制准入函数。
     * @param referencePlane 当前草图参考面。
     * @param axisGeometry 本次镜像轴世界几何。
     * @param upstreamPredicate 上游领域过滤规则。
     * @returns 同时满足上游规则且反射后可原样成立的准入函数。
     */
    public createReflectionClonePredicate(
        referencePlane: ReferencePlane,
        axisGeometry: SketchMirrorAxisResolvedGeometry,
        upstreamPredicate: (constraint: SketchRelationDefinition<ISketchObject>) => boolean,
    ): (constraint: SketchRelationDefinition<ISketchObject>) => boolean {
        return (constraint) => upstreamPredicate(constraint)
            && this.transformConstraintPolicy.shouldCloneDirectlyForReflection(
                constraint,
                referencePlane,
                axisGeometry,
            );
    }

    /**
     * 为镜像副本补建反射后换型的水平或竖直关系。
     * @param sourceEntities 本次镜向选择域内的源实体。
     * @param sourceObjectCloneMap 源对象 UUID 到最终副本对象映射。
     * @param referencePlane 当前草图参考面。
     * @param axisGeometry 本次镜像轴世界几何。
     */
    public migrateClonedReflectionRelations(
        sourceEntities: readonly THREE.Object3D[],
        sourceObjectCloneMap: ReadonlyMap<string, THREE.Object3D>,
        referencePlane: ReferencePlane,
        axisGeometry: SketchMirrorAxisResolvedGeometry,
    ): void {
        const sourceOwnerMap = new Map<string, ISketchObject>();
        sourceEntities.forEach((entity) => this.collectConstraintOwnerDomain(entity, sourceOwnerMap));

        sourceObjectCloneMap.forEach((cloneObject, sourceObjectId) => {
            const sourceObject = sourceOwnerMap.get(sourceObjectId) ?? null;
            if (!sourceObject || !isSketchLineObject(sourceObject) || !isSketchLineObject(cloneObject)) {
                return;
            }
            sourceObject.constraints.getRelations().forEach((relation) => {
                const normalizedRelation = relation as SketchRelationDefinition<ISketchObject>;
                const reflectedKind = this.transformConstraintPolicy.resolveReflectedRelationKind(
                    normalizedRelation,
                    referencePlane,
                    axisGeometry,
                );
                if (reflectedKind === normalizedRelation.relationType || reflectedKind === null) {
                    return;
                }
                const migratedRelation = this.createDirectionalRelation(
                    reflectedKind,
                    normalizedRelation,
                    false,
                );
                if (migratedRelation) {
                    cloneObject.constraints.addRelation(migratedRelation);
                }
            });
        });
    }

    /**
     * 按指定模式断开几何关系。
     *
     * @param input 几何关系保留输入。
     * @returns 可提交或回滚的关系移除上下文。
     */
    private detachRelations(
        input: SketchRelationRetentionInput,
        referencePlane?: ReferencePlane,
        axisGeometry?: SketchMirrorAxisResolvedGeometry,
    ): SketchRelationRetentionContext {
        const selectedObjectIds = this.collectSelectedRelationObjectIds(input.selectedEntities);
        const constraintOwners = this.collectConstraintOwners(input.sketchGroup);
        const removedRecords = this.collectRelationRecords(
            constraintOwners,
            selectedObjectIds,
            input.detachMode ?? SketchRelationRetentionDetachMode.ExternalRelations,
            referencePlane,
            axisGeometry,
        );

        removedRecords.forEach((record) => {
            record.owners.forEach((owner) => {
                // 同一约束实例会挂在多个目标对象上，必须从全部宿主容器中移除，避免留下半断开的关系。
                owner.constraints.removeRelation(record.constraint.id);
            });
        });

        const migratedRelationRecords: Array<readonly [
            SketchRelationDefinition<ISketchObject>,
            readonly ISketchObject[],
        ]> = [];
        if (referencePlane && axisGeometry) {
            removedRecords.forEach((record) => {
                const reflectedKind = this.transformConstraintPolicy.resolveReflectedRelationKind(
                    record.constraint,
                    referencePlane,
                    axisGeometry,
                );
                if (reflectedKind === null || reflectedKind === record.constraint.relationType) {
                    return;
                }
                const migratedRelation = this.createDirectionalRelation(reflectedKind, record.constraint);
                if (!migratedRelation) {
                    return;
                }
                const targetOwners = record.owners.filter(isSketchLineObject);
                targetOwners.forEach((owner) => owner.constraints.addRelation(migratedRelation));
                migratedRelationRecords.push([migratedRelation, targetOwners]);
            });
        }

        const detachedReferenceTargets = new Set(
            removedRecords.flatMap((record) => record.owners)
                .filter(isSketchMirrorAxisReferenceProvider)
                .filter((referenceTarget) => referenceTarget.constraints.getRelations().length === 0),
        );
        detachedReferenceTargets.forEach((referenceTarget) => {
            // 预览回滚仍需原 provider，只有先退出主状态，不能提前释放 resolver。
            input.sketchGroup.removeSketchReferenceTarget(referenceTarget);
        });

        let settled = false;
        return {
            removedConstraintCount: removedRecords.length,
            commit(): void {
                if (settled) {
                    return;
                }
                settled = true;
                detachedReferenceTargets.forEach((referenceTarget) => referenceTarget.dispose());
            },
            rollback(): void {
                if (settled) {
                    return;
                }
                settled = true;
                migratedRelationRecords.forEach(([relation, owners]) => {
                    owners.forEach((owner) => {
                        if (input.sketchGroup.getConstraintTargetByPersistentId(owner.persistentId) === owner) {
                            owner.constraints.removeRelation(relation.id);
                        }
                    });
                });
                detachedReferenceTargets.forEach((referenceTarget) => {
                    const currentTarget = input.sketchGroup.getConstraintTargetByPersistentId(
                        referenceTarget.persistentId,
                    );
                    if (currentTarget === null) {
                        input.sketchGroup.addSketchReferenceTarget(referenceTarget);
                        return;
                    }
                    if (currentTarget !== referenceTarget) {
                        // 历史层已恢复新的权威 provider，旧实例不得重复注册。
                        referenceTarget.dispose();
                    }
                });
                removedRecords.forEach((record) => {
                    const canRestoreOriginalOwners = record.owners.every((owner) => (
                        input.sketchGroup.getConstraintTargetByPersistentId(owner.persistentId) === owner
                    ));
                    if (!canRestoreOriginalOwners) {
                        return;
                    }
                    record.owners.forEach((owner) => {
                        // 回滚使用原约束实例恢复到原宿主，保持约束 ID、求解状态和显示名称不变。
                        owner.constraints.addRelation(
                            record.constraint as Parameters<typeof owner.constraints.addRelation>[0],
                        );
                    });
                });
                detachedReferenceTargets.forEach((referenceTarget) => {
                    if (input.sketchGroup.hasSketchReferenceTarget(referenceTarget)
                        && referenceTarget.constraints.getRelations().length === 0) {
                        input.sketchGroup.removeSketchReferenceTarget(referenceTarget);
                        referenceTarget.dispose();
                    }
                });
            },
        };
    }

    /**
     * 收集选择域中参与关系边界判断的对象 ID。
     *
     * @param selectedEntities 当前选择实体。
     * @returns 选择域内对象 ID 集合。
     */
    private collectSelectedRelationObjectIds(selectedEntities: readonly THREE.Object3D[]): Set<string> {
        const selectedObjectIds = new Set<string>();
        selectedEntities.forEach((entity) => {
            this.collectEntityRelationObjects(entity, selectedObjectIds);
        });
        return selectedObjectIds;
    }

    /**
     * 递归收集单个实体在关系语义中的组成对象。
     *
     * @param entity 当前实体。
     * @param selectedObjectIds 选择域对象 ID 集合。
     */
    private collectEntityRelationObjects(entity: THREE.Object3D, selectedObjectIds: Set<string>): void {
        if (isSketchObject(entity)) {
            selectedObjectIds.add(entity.uuid);
        }

        if (isAbstractSketchEdgeObject(entity)) {
            entity.relatedSketchPoints.forEach((point) => {
                selectedObjectIds.add(point.uuid);
            });
        }

        if (isSketchCollectionObject(entity)) {
            entity.collectSet.forEach((source) => {
                this.collectEntityRelationObjects(source, selectedObjectIds);
            });
        }

        if (isSketchSlotObject(entity)) {
            entity.getEdges().forEach((edge) => {
                this.collectEntityRelationObjects(edge, selectedObjectIds);
            });
            if (entity.constructionCenterline) {
                this.collectEntityRelationObjects(entity.constructionCenterline, selectedObjectIds);
            }
            if (entity.constructionCenterlineArc) {
                this.collectEntityRelationObjects(entity.constructionCenterlineArc, selectedObjectIds);
            }
        }

        entity.traverse((child) => {
            if (!isSketchPointObject(child) && !isAbstractSketchEdgeObject(child)) {
                return;
            }
            selectedObjectIds.add(child.uuid);
        });
    }

    /**
     * 收集当前草图组内可承载约束的对象。
     *
     * @param sketchGroup 当前草图组。
     * @returns 约束宿主对象列表。
     */
    private collectConstraintOwners(sketchGroup: SketchGroup): ISketchObject[] {
        const ownerMap = new Map<string, ISketchObject>();
        sketchGroup.getSketchObjects().forEach((object) => {
            this.collectConstraintOwnerDomain(object, ownerMap);
        });
        sketchGroup.getSketchReferenceTargets().forEach((referenceTarget) => {
            ownerMap.set(referenceTarget.uuid, referenceTarget);
        });
        return Array.from(ownerMap.values());
    }

    /**
     * 收集实体及隐藏控制点、复合成员中的全部关系宿主。
     * @param entity 当前实体。
     * @param ownerMap 已按 UUID 去重的宿主映射。
     */
    private collectConstraintOwnerDomain(
        entity: THREE.Object3D,
        ownerMap: Map<string, ISketchObject>,
    ): void {
        if (isSketchObject(entity) && isSketchRelationContainer(entity.constraints)) {
            ownerMap.set(entity.uuid, entity);
        }
        if (isAbstractSketchEdgeObject(entity)) {
            entity.relatedSketchPoints.forEach((point) => ownerMap.set(point.uuid, point));
        }
        if (isSketchCollectionObject(entity)) {
            entity.collectSet.forEach((source) => this.collectConstraintOwnerDomain(source, ownerMap));
        }
        if (isSketchSlotObject(entity)) {
            entity.getEdges().forEach((edge) => this.collectConstraintOwnerDomain(edge, ownerMap));
            if (entity.constructionCenterline) {
                this.collectConstraintOwnerDomain(entity.constructionCenterline, ownerMap);
            }
            if (entity.constructionCenterlineArc) {
                this.collectConstraintOwnerDomain(entity.constructionCenterlineArc, ownerMap);
            }
        }
        entity.traverse((child) => {
            if (child === entity || !isSketchObject(child) || !isSketchRelationContainer(child.constraints)) {
                return;
            }
            ownerMap.set(child.uuid, child);
        });
    }

    /**
     * 收集需要断开的跨选择域关系。
     *
     * @param constraintOwners 当前草图内约束宿主。
     * @param selectedObjectIds 选择域对象 ID 集合。
     * @returns 需要移除的约束记录。
     */
    private collectRelationRecords(
        constraintOwners: readonly ISketchObject[],
        selectedObjectIds: ReadonlySet<string>,
        detachMode: SketchRelationRetentionDetachMode,
        referencePlane?: ReferencePlane,
        axisGeometry?: SketchMirrorAxisResolvedGeometry,
    ): RemovedConstraintRecord[] {
        const ownerMap = new Map<SketchRelationDefinition<ISketchObject>, ISketchObject[]>();
        constraintOwners.forEach((owner) => {
            owner.constraints.getRelations().forEach((constraint) => {
                const normalizedConstraint = constraint as SketchRelationDefinition<ISketchObject>;
                const owners = ownerMap.get(normalizedConstraint) ?? [];
                owners.push(owner);
                ownerMap.set(normalizedConstraint, owners);
            });
        });

        return Array.from(ownerMap.entries())
            .filter(([constraint]) => this.shouldBreakRelation(
                constraint,
                selectedObjectIds,
                detachMode,
                referencePlane,
                axisGeometry,
            ))
            .map(([constraint, owners]) => ({
                constraint,
                owners,
            }));
    }

    /**
     * 判断约束是否应按当前模式断开。
     *
     * @param constraint 待判断约束。
     * @param selectedObjectIds 选择域对象 ID 集合。
     * @param detachMode 当前断开模式。
     * @returns 若约束需要断开则返回 true。
     */
    private shouldBreakRelation(
        constraint: SketchRelationDefinition<ISketchObject>,
        selectedObjectIds: ReadonlySet<string>,
        detachMode: SketchRelationRetentionDetachMode,
        referencePlane?: ReferencePlane,
        axisGeometry?: SketchMirrorAxisResolvedGeometry,
    ): boolean {
        if (this.shouldBreakExternalRelation(constraint, selectedObjectIds)) {
            return true;
        }
        if (detachMode !== SketchRelationRetentionDetachMode.RotateBlockingRelations) {
            return detachMode === SketchRelationRetentionDetachMode.ReflectionVariantRelations
                && referencePlane !== undefined
                && axisGeometry !== undefined
                && this.transformConstraintPolicy.shouldDetachBeforeReflection(
                    constraint,
                    selectedObjectIds,
                    referencePlane,
                    axisGeometry,
                );
        }
        return this.shouldBreakRotateBlockingRelation(constraint, selectedObjectIds);
    }

    /**
     * 判断约束是否跨越当前选择域边界。
     *
     * @param constraint 待判断约束。
     * @param selectedObjectIds 选择域对象 ID 集合。
     * @returns 若约束需要断开则返回 true。
     */
    private shouldBreakExternalRelation(
        constraint: SketchRelationDefinition<ISketchObject>,
        selectedObjectIds: ReadonlySet<string>,
    ): boolean {
        const targets = constraint.getTargetsSnapshot();
        if (targets.length <= 1) {
            return false;
        }

        let hasSelectedTarget = false;
        let hasExternalTarget = false;
        targets.forEach((target) => {
            if (selectedObjectIds.has(target.uuid)) {
                hasSelectedTarget = true;
                return;
            }
            hasExternalTarget = true;
        });
        return hasSelectedTarget && hasExternalTarget;
    }

    /**
     * 判断约束是否会阻止旋转实体的刚体旋转。
     *
     * @param constraint 待判断约束。
     * @param selectedObjectIds 选择域对象 ID 集合。
     * @returns 若约束需要断开则返回 true。
     *
     * 设计说明：
     * 1. 水平、竖直、固定和固定槽口都是相对草图坐标轴或绝对位置的自关系；
     * 2. Rotate Entities 默认不保留几何关系时，这些关系会把所选矩形拉回原方向，必须在旋转前断开；
     * 3. 平行、垂直、重合、相等这类选择域内部形状关系不在这里删除，避免破坏闭合轮廓设计意图。
     */
    private shouldBreakRotateBlockingRelation(
        constraint: SketchRelationDefinition<ISketchObject>,
        selectedObjectIds: ReadonlySet<string>,
    ): boolean {
        if (!ROTATE_BLOCKING_SELF_RELATION_KINDS.has(constraint.relationType)) {
            return false;
        }

        return constraint.getTargetsSnapshot().some((target) => selectedObjectIds.has(target.uuid));
    }

    /**
     * 创建反射换型后的水平或竖直关系并继承求解状态。
     * @param relationKind 目标关系类型。
     * @param sourceRelation 原关系实例。
     * @param preservePersistentIdentity 是否延续原关系持久身份。
     * @returns 新关系；目标类型不是水平或竖直时返回 null。
     */
    private createDirectionalRelation(
        relationKind: SketchRelationKind,
        sourceRelation: SketchRelationDefinition<ISketchObject>,
        preservePersistentIdentity = true,
    ): SketchRelationDefinition<ISketchObject> | null {
        const migratedRelation = relationKind === SketchRelationKind.Horizontal
            ? new LineHorizontalConstraint()
            : relationKind === SketchRelationKind.Vertical
                ? new LineVerticalConstraint()
                : null;
        if (!migratedRelation) {
            return null;
        }
        migratedRelation.setSuppressed(sourceRelation.isSuppressed);
        migratedRelation.setBranchHint(sourceRelation.getBranchHint());
        if (sourceRelation.isDangling && sourceRelation.danglingDiagnosticMessage) {
            migratedRelation.markDangling(sourceRelation.danglingDiagnosticMessage);
        }
        if (preservePersistentIdentity) {
            migratedRelation.persistentId = sourceRelation.persistentId;
        }
        return migratedRelation as SketchRelationDefinition<ISketchObject>;
    }
}
//------ 移动实体保留几何关系 结束------
